/**
 * SC-09: 計画スナップショット管理画面
 *
 * スナップショットの一覧・作成・削除・比較機能を提供
 *
 * @created 2025-12-15
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  Collapse,
} from '@mui/material';
import {
  Add,
  Delete,
  Compare,
  ExpandMore,
  ExpandLess,
  PhotoCamera,
  Info,
  Print,
} from '@mui/icons-material';
import { salesSnapshotApi } from '../../services/salesApi';
import type { PlanSnapshot, SnapshotCase, SnapshotComparison } from '../../services/salesApi';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatCurrency } from '../../utils/formatNumber';
import { formatDateJST, formatDateTimeJST } from '../../utils/dateUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const SalesSnapshotsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // タブ（URLパラメータから初期値を取得）
  const [tabValue, setTabValue] = useState(() => {
    const tab = searchParams.get('tab');
    return tab ? parseInt(tab) : 0;
  });

  // 共通
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // スナップショット一覧
  const [snapshots, setSnapshots] = useState<PlanSnapshot[]>([]);
  const [fiscalYearFilter, setFiscalYearFilter] = useState<number | ''>('');
  const [typeFilter, setTypeFilter] = useState<'manual' | 'auto_weekly' | ''>('');

  // 作成ダイアログ
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [snapshotName, setSnapshotName] = useState('');
  const [snapshotDescription, setSnapshotDescription] = useState('');

  // 詳細表示
  const [expandedSnapshotId, setExpandedSnapshotId] = useState<number | null>(null);
  const [snapshotCases, setSnapshotCases] = useState<SnapshotCase[]>([]);
  const [loadingCases, setLoadingCases] = useState(false);

  // 削除ダイアログ
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PlanSnapshot | null>(null);

  // 比較機能（URLパラメータから初期値を取得）
  const [comparison, setComparison] = useState<SnapshotComparison | null>(null);
  const [compareSnapshot1, setCompareSnapshot1] = useState<number | 'current'>(() => {
    const s1 = searchParams.get('s1');
    return s1 === 'current' ? 'current' : s1 ? parseInt(s1) : 'current';
  });
  const [compareSnapshot2, setCompareSnapshot2] = useState<number | 'current'>(() => {
    const s2 = searchParams.get('s2');
    return s2 === 'current' ? 'current' : s2 ? parseInt(s2) : 'current';
  });
  const [compareLoading, setCompareLoading] = useState(false);

  // URLパラメータを更新する関数
  const updateUrlParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null) {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // 年度オプション
  const currentFiscalYear = new Date().getMonth() >= 3
    ? new Date().getFullYear()
    : new Date().getFullYear() - 1;
  const fiscalYearOptions = Array.from({ length: 5 }, (_, i) => currentFiscalYear + 1 - i);

  // データ取得
  const fetchSnapshots = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params: { fiscalYear?: number; type?: 'manual' | 'auto_weekly' } = {};
      if (fiscalYearFilter) params.fiscalYear = fiscalYearFilter;
      if (typeFilter) params.type = typeFilter;

      const response = await salesSnapshotApi.getSnapshots(params);
      if (response.data.success) {
        setSnapshots(response.data.data);
      }
    } catch (err) {
      console.error('スナップショット取得エラー:', err);
      setError(extractErrorMessage(err, 'スナップショットの取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  }, [fiscalYearFilter, typeFilter]);

  useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  // スナップショット詳細取得
  const handleExpand = async (snapshotId: number) => {
    if (expandedSnapshotId === snapshotId) {
      setExpandedSnapshotId(null);
      setSnapshotCases([]);
      return;
    }

    try {
      setLoadingCases(true);
      const response = await salesSnapshotApi.getSnapshot(snapshotId);
      if (response.data.success) {
        setSnapshotCases(response.data.data.cases);
        setExpandedSnapshotId(snapshotId);
      }
    } catch (err) {
      console.error('スナップショット詳細取得エラー:', err);
      setError(extractErrorMessage(err, 'スナップショット詳細の取得に失敗しました'));
    } finally {
      setLoadingCases(false);
    }
  };

  // スナップショット作成
  const handleCreate = async () => {
    if (!snapshotName.trim()) {
      setError('スナップショット名を入力してください');
      return;
    }

    try {
      setLoading(true);
      const response = await salesSnapshotApi.createSnapshot({
        snapshotName: snapshotName.trim(),
        description: snapshotDescription.trim() || undefined,
      });
      if (response.data.success) {
        setSuccess('スナップショットを作成しました');
        setCreateDialogOpen(false);
        setSnapshotName('');
        setSnapshotDescription('');
        fetchSnapshots();
      }
    } catch (err) {
      console.error('スナップショット作成エラー:', err);
      setError(extractErrorMessage(err, 'スナップショットの作成に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  // スナップショット削除
  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setLoading(true);
      const response = await salesSnapshotApi.deleteSnapshot(deleteTarget.snapshotId);
      if (response.data.success) {
        setSuccess('スナップショットを削除しました');
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
        fetchSnapshots();
      }
    } catch (err) {
      console.error('スナップショット削除エラー:', err);
      setError(extractErrorMessage(err, 'スナップショットの削除に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  // スナップショット比較
  const handleCompare = useCallback(async () => {
    try {
      setCompareLoading(true);
      setError('');
      const response = await salesSnapshotApi.compareSnapshots({
        snapshot1: compareSnapshot1,
        snapshot2: compareSnapshot2,
        fiscalYear: currentFiscalYear + 1,
      });
      if (response.data.success) {
        setComparison(response.data.data);
      }
    } catch (err) {
      console.error('スナップショット比較エラー:', err);
      setError(extractErrorMessage(err, 'スナップショットの比較に失敗しました'));
    } finally {
      setCompareLoading(false);
    }
  }, [compareSnapshot1, compareSnapshot2, currentFiscalYear]);

  // URLパラメータから復元された場合、自動的に比較を実行
  useEffect(() => {
    const hasUrlParams = searchParams.get('s1') || searchParams.get('s2');
    const isCompareTab = tabValue === 1;
    
    // 比較タブで、URLパラメータがある場合は自動実行
    if (isCompareTab && hasUrlParams && !comparison && !compareLoading) {
      handleCompare();
    }
  }, [tabValue, searchParams, comparison, compareLoading, handleCompare]);

  // 日付フォーマット
  const formatDate = (dateStr: string) => {
    return formatDateJST(dateStr);
  };

  // 差分カラー
  const getDiffColor = (diff: number) => {
    if (diff > 0) return 'success.main';
    if (diff < 0) return 'error.main';
    return 'text.primary';
  };

  // レポート出力
  const handleExportReport = useCallback(() => {
    if (!comparison) return;

    const diffColorCss = (diff: number) => {
      if (diff > 0) return 'color: #2e7d32;';
      if (diff < 0) return 'color: #d32f2f;';
      return '';
    };

    const fmtCurrency = (val: number) => {
      return '¥' + Math.abs(val).toLocaleString('ja-JP');
    };

    const fmtDiff = (val: number) => {
      const sign = val >= 0 ? '+' : '-';
      return sign + '¥' + Math.abs(val).toLocaleString('ja-JP');
    };

    const bd = comparison.summary.breakdown;
    const breakdownItems: Array<{ label: string; count: number; salesImpact: number; costImpact: number; profitImpact: number }> = [];
    if (bd) {
      if (bd.newCases.count > 0) breakdownItems.push({ label: '新規話題', ...bd.newCases });
      if (bd.salesChanges.count > 0) breakdownItems.push({ label: '売上変更', ...bd.salesChanges });
      if (bd.costChanges.count > 0) breakdownItems.push({ label: '原価変更', ...bd.costChanges });
      if (bd.wonCases.count > 0) breakdownItems.push({ label: '受注', ...bd.wonCases });
      if (bd.lostCases.count > 0) breakdownItems.push({ label: '失注', ...bd.lostCases });
      if (bd.removedCases.count > 0) breakdownItems.push({ label: '削除', ...bd.removedCases });
      if (bd.suspendedCases.count > 0) breakdownItems.push({ label: '保留', ...bd.suspendedCases });
    }

    const breakdownHtml = breakdownItems.length > 0 ? `
      <div class="section">
        <h2>変更原因別内訳</h2>
        <p>案件数: ${comparison.summary.caseCount1 ?? 0}件 → ${comparison.summary.caseCount2 ?? 0}件
          ${(comparison.summary.caseCountDiff ?? 0) !== 0 ? `<span style="${diffColorCss(comparison.summary.caseCountDiff ?? 0)} font-weight:bold;">(${(comparison.summary.caseCountDiff ?? 0) >= 0 ? '+' : ''}${comparison.summary.caseCountDiff}件)</span>` : ''}
        </p>
        <table class="breakdown-table">
          <thead><tr><th>原因</th><th>件数</th><th>売上影響</th><th>原価影響</th><th>粗利影響</th></tr></thead>
          <tbody>
            ${breakdownItems.map(item => `
              <tr>
                <td>${item.label}</td>
                <td style="text-align:right;">${item.label === '新規話題' ? '+' : ''}${item.count}件</td>
                <td style="text-align:right; ${diffColorCss(item.salesImpact)}">${fmtDiff(item.salesImpact)}</td>
                <td style="text-align:right; ${diffColorCss(-item.costImpact)}">${fmtDiff(item.costImpact)}</td>
                <td style="text-align:right; ${diffColorCss(item.profitImpact)}">${fmtDiff(item.profitImpact)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        ${(comparison.summary.totalActivityCount ?? 0) > 0 ? `<p style="margin-top:8px; color:#666;">期間中の行動履歴: 合計${comparison.summary.totalActivityCount}件</p>` : ''}
      </div>
    ` : '';

    const casesHtml = comparison.cases.length > 0 ? `
      <div class="section">
        <h2>案件別差分（売上差分の金額順）</h2>
        <table class="cases-table">
          <thead>
            <tr>
              <th>案件番号</th>
              <th>案件名</th>
              <th style="text-align:right;">${comparison.snapshot1.name}</th>
              <th style="text-align:right;">${comparison.snapshot2.name}</th>
              <th style="text-align:right;">売上差分</th>
              <th style="text-align:right;">粗利(${comparison.snapshot1.name})</th>
              <th style="text-align:right;">粗利(${comparison.snapshot2.name})</th>
              <th style="text-align:right;">粗利差分</th>
              <th style="text-align:center;">行動履歴</th>
              <th>変更原因</th>
            </tr>
          </thead>
          <tbody>
            ${comparison.cases.map(c => `
              <tr>
                <td>${c.caseNo}</td>
                <td>${c.caseName}</td>
                <td style="text-align:right;">${fmtCurrency(c.sales1)}</td>
                <td style="text-align:right;">${fmtCurrency(c.sales2)}</td>
                <td style="text-align:right; font-weight:bold; ${diffColorCss(c.salesDiff)}">${fmtDiff(c.salesDiff)}</td>
                <td style="text-align:right;">${fmtCurrency(c.profit1)}</td>
                <td style="text-align:right;">${fmtCurrency(c.profit2)}</td>
                <td style="text-align:right; font-weight:bold; ${diffColorCss(c.profitDiff)}">${fmtDiff(c.profitDiff)}</td>
                <td style="text-align:center;">${(c.activityCount ?? 0) > 0 ? `${c.activityCount}件` : '0件'}</td>
                <td>${(c.changeReasons || []).join(', ')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    ` : '<div class="section"><h2>案件別差分</h2><p>差分のある案件はありません。</p></div>';

    const activities = comparison.summary.activities || [];
    const activitiesHtml = activities.length > 0 ? `
      <div class="section">
        <h2>行動履歴明細（${activities.length}件）</h2>
        <table class="cases-table">
          <thead>
            <tr>
              <th>日付</th>
              <th>案件番号</th>
              <th>案件名</th>
              <th>種別</th>
              <th>内容</th>
              <th>記録者</th>
            </tr>
          </thead>
          <tbody>
            ${activities.map(act => `
              <tr>
                <td style="white-space:nowrap;">${formatDateJST(act.activityDate)}</td>
                <td style="white-space:nowrap;">${act.caseNo}</td>
                <td>${act.caseName}</td>
                <td>${act.activityType}</td>
                <td>${act.description || '-'}</td>
                <td style="white-space:nowrap;">${act.createdByName}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    ` : '';

    const reportHtml = `<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <title>スナップショット比較レポート - ${comparison.snapshot1.name} vs ${comparison.snapshot2.name}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: "Yu Gothic", "Hiragino Kaku Gothic ProN", "メイリオ", sans-serif;
      color: #333;
      padding: 30px 40px;
      background: #fff;
      font-size: 12px;
    }
    .header {
      border-bottom: 3px solid #1976d2;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }
    .header h1 { font-size: 20px; color: #1976d2; margin-bottom: 6px; }
    .header .meta { font-size: 11px; color: #666; }
    .summary-cards {
      display: flex;
      gap: 16px;
      margin-bottom: 24px;
    }
    .summary-card {
      flex: 1;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      padding: 14px 16px;
    }
    .summary-card h3 { font-size: 12px; color: #666; margin-bottom: 8px; }
    .summary-card .row { display: flex; justify-content: space-between; margin-bottom: 4px; }
    .summary-card .row.diff { border-top: 1px solid #e0e0e0; padding-top: 6px; margin-top: 4px; }
    .summary-card .row .label { color: #666; }
    .summary-card .row .value { font-weight: bold; }
    .section { margin-bottom: 24px; }
    .section h2 {
      font-size: 14px;
      color: #333;
      border-left: 4px solid #1976d2;
      padding-left: 10px;
      margin-bottom: 12px;
    }
    .section p { font-size: 12px; color: #555; margin-bottom: 8px; }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 11px;
    }
    th {
      background: #f5f5f5;
      border: 1px solid #ddd;
      padding: 6px 8px;
      font-weight: 600;
      text-align: left;
      white-space: nowrap;
    }
    td {
      border: 1px solid #ddd;
      padding: 5px 8px;
    }
    .breakdown-table { max-width: 700px; }
    .cases-table td:nth-child(2) { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    @media print {
      @page { size: A4 landscape; margin: 15mm; }
      body { padding: 0; font-size: 10px; }
      .header h1 { font-size: 16px; }
      table { font-size: 9px; }
      .summary-cards { gap: 10px; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>スナップショット比較レポート</h1>
    <div class="meta">
      比較元: ${comparison.snapshot1.name}（${formatDate(comparison.snapshot1.date)}）
      &nbsp;→&nbsp;
      比較先: ${comparison.snapshot2.name}（${formatDate(comparison.snapshot2.date)}）
      &nbsp;|&nbsp;
      出力日時: ${formatDateTimeJST(new Date(), true)}
    </div>
  </div>

  <div class="summary-cards">
    <div class="summary-card">
      <h3>パイプライン売上</h3>
      <div class="row"><span class="label">${comparison.snapshot1.name}</span><span class="value">${fmtCurrency(comparison.summary.totalSales1)}</span></div>
      <div class="row"><span class="label">${comparison.snapshot2.name}</span><span class="value">${fmtCurrency(comparison.summary.totalSales2)}</span></div>
      <div class="row diff"><span class="label">差分</span><span class="value" style="${diffColorCss(comparison.summary.salesDiff)}">${fmtDiff(comparison.summary.salesDiff)} (${comparison.summary.salesDiffPercent}%)</span></div>
    </div>
    <div class="summary-card">
      <h3>パイプライン原価</h3>
      <div class="row"><span class="label">${comparison.snapshot1.name}</span><span class="value">${fmtCurrency(comparison.summary.totalCost1)}</span></div>
      <div class="row"><span class="label">${comparison.snapshot2.name}</span><span class="value">${fmtCurrency(comparison.summary.totalCost2)}</span></div>
      <div class="row diff"><span class="label">差分</span><span class="value" style="${diffColorCss(-comparison.summary.costDiff)}">${fmtDiff(comparison.summary.costDiff)}</span></div>
    </div>
    <div class="summary-card">
      <h3>パイプライン粗利</h3>
      <div class="row"><span class="label">${comparison.snapshot1.name}</span><span class="value">${fmtCurrency(comparison.summary.totalProfit1)}</span></div>
      <div class="row"><span class="label">${comparison.snapshot2.name}</span><span class="value">${fmtCurrency(comparison.summary.totalProfit2)}</span></div>
      <div class="row diff"><span class="label">差分</span><span class="value" style="${diffColorCss(comparison.summary.profitDiff)}">${fmtDiff(comparison.summary.profitDiff)}</span></div>
    </div>
  </div>

  ${breakdownHtml}
  ${casesHtml}
  ${activitiesHtml}
</body>
</html>`;

    const reportWindow = window.open('', '_blank');
    if (reportWindow) {
      reportWindow.document.write(reportHtml);
      reportWindow.document.close();
    }
  }, [comparison, formatDate]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">計画スナップショット管理</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
        >
          スナップショット作成
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(_, v) => { setTabValue(v); updateUrlParams({ tab: v.toString() }); }} sx={{ mb: 2 }}>
        <Tab label="スナップショット一覧" />
        <Tab label="スナップショット比較" />
      </Tabs>

      {/* 一覧タブ */}
      <TabPanel value={tabValue} index={0}>
        {/* フィルター */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>年度</InputLabel>
            <Select
              value={fiscalYearFilter}
              label="年度"
              onChange={(e) => setFiscalYearFilter(e.target.value as number | '')}
            >
              <MenuItem value="">すべて</MenuItem>
              {fiscalYearOptions.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}年度
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>タイプ</InputLabel>
            <Select
              value={typeFilter}
              label="タイプ"
              onChange={(e) => setTypeFilter(e.target.value as 'manual' | 'auto_weekly' | '')}
            >
              <MenuItem value="">すべて</MenuItem>
              <MenuItem value="manual">手動</MenuItem>
              <MenuItem value="auto_weekly">自動（週次）</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : snapshots.length === 0 ? (
          <Alert severity="info">スナップショットがありません</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>スナップショット名</TableCell>
                  <TableCell>日付</TableCell>
                  <TableCell>年度</TableCell>
                  <TableCell>タイプ</TableCell>
                  <TableCell align="right">案件数</TableCell>
                  <TableCell align="right">パイプライン売上</TableCell>
                  <TableCell align="right">パイプライン粗利</TableCell>
                  <TableCell width={100}>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {snapshots.map((snapshot) => (
                  <React.Fragment key={snapshot.snapshotId}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleExpand(snapshot.snapshotId)}
                        >
                          {expandedSnapshotId === snapshot.snapshotId ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhotoCamera fontSize="small" color="action" />
                          {snapshot.snapshotName}
                          {snapshot.description && (
                            <Tooltip title={snapshot.description}>
                              <Info fontSize="small" color="action" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(snapshot.snapshotDate)}</TableCell>
                      <TableCell>{snapshot.fiscalYear}年度</TableCell>
                      <TableCell>
                        <Chip
                          label={snapshot.snapshotType === 'manual' ? '手動' : '週次'}
                          size="small"
                          color={snapshot.snapshotType === 'manual' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{snapshot.caseCount}件</TableCell>
                      <TableCell align="right">
                        {formatCurrency(snapshot.totalPlannedSales)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(snapshot.totalPlannedProfit)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="削除">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setDeleteTarget(snapshot);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 0 }}>
                        <Collapse
                          in={expandedSnapshotId === snapshot.snapshotId}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 2, px: 4, bgcolor: 'grey.50' }}>
                            {loadingCases ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>案件番号</TableCell>
                                    <TableCell>案件名</TableCell>
                                    <TableCell>顧客</TableCell>
                                    <TableCell>担当者</TableCell>
                                    <TableCell>フェーズ</TableCell>
                                    <TableCell align="right">パイプライン売上</TableCell>
                                    <TableCell align="right">パイプライン粗利</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {snapshotCases.slice(0, 10).map((c) => (
                                    <TableRow key={c.caseId}>
                                      <TableCell>{c.caseNo}</TableCell>
                                      <TableCell>{c.caseName}</TableCell>
                                      <TableCell>{c.customerName || '-'}</TableCell>
                                      <TableCell>{c.ownerName || '-'}</TableCell>
                                      <TableCell>{c.phaseName || '-'}</TableCell>
                                      <TableCell align="right">
                                        {formatCurrency(c.plannedSales)}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatCurrency(c.plannedProfit)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {snapshotCases.length > 10 && (
                                    <TableRow>
                                      <TableCell colSpan={7} align="center">
                                        <Typography variant="caption" color="text.secondary">
                                          他 {snapshotCases.length - 10} 件
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* 比較タブ */}
      <TabPanel value={tabValue} index={1}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              比較設定
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>比較元</InputLabel>
                  <Select
                    value={compareSnapshot1}
                    label="比較元"
                    onChange={(e) => { const v = e.target.value as number | 'current'; setCompareSnapshot1(v); updateUrlParams({ s1: v.toString() }); }}
                  >
                    <MenuItem value="current">現在の計画</MenuItem>
                    {snapshots.map((s) => (
                      <MenuItem key={s.snapshotId} value={s.snapshotId}>
                        {s.snapshotName} ({formatDate(s.snapshotDate)})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1} sx={{ textAlign: 'center' }}>
                <Compare color="action" />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>比較先</InputLabel>
                  <Select
                    value={compareSnapshot2}
                    label="比較先"
                    onChange={(e) => { const v = e.target.value as number | 'current'; setCompareSnapshot2(v); updateUrlParams({ s2: v.toString() }); }}
                  >
                    <MenuItem value="current">現在の計画</MenuItem>
                    {snapshots.map((s) => (
                      <MenuItem key={s.snapshotId} value={s.snapshotId}>
                        {s.snapshotName} ({formatDate(s.snapshotDate)})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Compare />}
                  onClick={handleCompare}
                  disabled={compareLoading}
                >
                  {compareLoading ? <CircularProgress size={20} /> : '比較実行'}
                </Button>
                {comparison && (
                  <Button
                    variant="outlined"
                    startIcon={<Print />}
                    onClick={handleExportReport}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    レポート出力
                  </Button>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {comparison && (
          <>
            {/* サマリー */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      パイプライン売上
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{comparison.snapshot1.name}</Typography>
                      <Typography variant="body1">
                        {formatCurrency(comparison.summary.totalSales1)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{comparison.snapshot2.name}</Typography>
                      <Typography variant="body1">
                        {formatCurrency(comparison.summary.totalSales2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        pt: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2">差分</Typography>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        sx={{ color: getDiffColor(comparison.summary.salesDiff) }}
                      >
                        {comparison.summary.salesDiff >= 0 ? '+' : ''}
                        {formatCurrency(comparison.summary.salesDiff)}
                        {' '}
                        ({comparison.summary.salesDiffPercent}%)
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      パイプライン原価
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{comparison.snapshot1.name}</Typography>
                      <Typography variant="body1">
                        {formatCurrency(comparison.summary.totalCost1)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{comparison.snapshot2.name}</Typography>
                      <Typography variant="body1">
                        {formatCurrency(comparison.summary.totalCost2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        pt: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2">差分</Typography>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        sx={{ color: getDiffColor(-comparison.summary.costDiff) }}
                      >
                        {comparison.summary.costDiff >= 0 ? '+' : ''}
                        {formatCurrency(comparison.summary.costDiff)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      パイプライン粗利
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{comparison.snapshot1.name}</Typography>
                      <Typography variant="body1">
                        {formatCurrency(comparison.summary.totalProfit1)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{comparison.snapshot2.name}</Typography>
                      <Typography variant="body1">
                        {formatCurrency(comparison.summary.totalProfit2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        pt: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2">差分</Typography>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        sx={{ color: getDiffColor(comparison.summary.profitDiff) }}
                      >
                        {comparison.summary.profitDiff >= 0 ? '+' : ''}
                        {formatCurrency(comparison.summary.profitDiff)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* 変更原因別内訳 */}
            {comparison.summary.breakdown && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    変更原因別内訳
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      案件数: {comparison.summary.caseCount1 ?? 0}件 → {comparison.summary.caseCount2 ?? 0}件
                      {(comparison.summary.caseCountDiff ?? 0) !== 0 && (
                        <Typography component="span" sx={{ color: getDiffColor(comparison.summary.caseCountDiff ?? 0), fontWeight: 'bold', ml: 1 }}>
                          ({(comparison.summary.caseCountDiff ?? 0) >= 0 ? '+' : ''}{comparison.summary.caseCountDiff}件)
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    {comparison.summary.breakdown.newCases.count > 0 && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="bold">新規話題</Typography>
                          <Typography variant="body2">+{comparison.summary.breakdown.newCases.count}件 売上 {comparison.summary.breakdown.newCases.salesImpact >= 0 ? '+' : ''}{formatCurrency(comparison.summary.breakdown.newCases.salesImpact)} 粗利 {comparison.summary.breakdown.newCases.profitImpact >= 0 ? '+' : ''}{formatCurrency(comparison.summary.breakdown.newCases.profitImpact)}</Typography>
                        </Box>
                      </Grid>
                    )}
                    {comparison.summary.breakdown.salesChanges.count > 0 && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="bold">売上変更</Typography>
                          <Typography variant="body2">{comparison.summary.breakdown.salesChanges.count}件 売上 {comparison.summary.breakdown.salesChanges.salesImpact >= 0 ? '+' : ''}{formatCurrency(comparison.summary.breakdown.salesChanges.salesImpact)} 粗利 {comparison.summary.breakdown.salesChanges.profitImpact >= 0 ? '+' : ''}{formatCurrency(comparison.summary.breakdown.salesChanges.profitImpact)}</Typography>
                        </Box>
                      </Grid>
                    )}
                    {comparison.summary.breakdown.costChanges.count > 0 && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="bold">原価変更</Typography>
                          <Typography variant="body2">{comparison.summary.breakdown.costChanges.count}件 原価 {comparison.summary.breakdown.costChanges.costImpact >= 0 ? '+' : ''}{formatCurrency(comparison.summary.breakdown.costChanges.costImpact)} 粗利 {comparison.summary.breakdown.costChanges.profitImpact >= 0 ? '+' : ''}{formatCurrency(comparison.summary.breakdown.costChanges.profitImpact)}</Typography>
                        </Box>
                      </Grid>
                    )}
                    {comparison.summary.breakdown.wonCases.count > 0 && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="bold">受注</Typography>
                          <Typography variant="body2">{comparison.summary.breakdown.wonCases.count}件 売上 {formatCurrency(comparison.summary.breakdown.wonCases.salesImpact)} 粗利 {formatCurrency(comparison.summary.breakdown.wonCases.profitImpact)}</Typography>
                        </Box>
                      </Grid>
                    )}
                    {comparison.summary.breakdown.lostCases.count > 0 && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="bold">失注</Typography>
                          <Typography variant="body2">{comparison.summary.breakdown.lostCases.count}件 売上 {formatCurrency(comparison.summary.breakdown.lostCases.salesImpact)} 粗利 {formatCurrency(comparison.summary.breakdown.lostCases.profitImpact)}</Typography>
                        </Box>
                      </Grid>
                    )}
                    {comparison.summary.breakdown.removedCases.count > 0 && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="bold">削除</Typography>
                          <Typography variant="body2">{comparison.summary.breakdown.removedCases.count}件 売上 {formatCurrency(comparison.summary.breakdown.removedCases.salesImpact)} 粗利 {formatCurrency(comparison.summary.breakdown.removedCases.profitImpact)}</Typography>
                        </Box>
                      </Grid>
                    )}
                    {comparison.summary.breakdown.suspendedCases.count > 0 && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="bold">保留</Typography>
                          <Typography variant="body2">{comparison.summary.breakdown.suspendedCases.count}件 売上 {formatCurrency(comparison.summary.breakdown.suspendedCases.salesImpact)} 粗利 {formatCurrency(comparison.summary.breakdown.suspendedCases.profitImpact)}</Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                  {(comparison.summary.totalActivityCount ?? 0) > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      期間中の行動履歴: 合計{comparison.summary.totalActivityCount}件
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {/* 案件別差分 */}
            <Card>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  案件別差分 ― パイプライン総額の差額（売上差分の金額順）
                </Typography>
                {comparison.cases.length === 0 ? (
                  <Alert severity="info">差分のある案件はありません</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>案件番号</TableCell>
                          <TableCell>案件名</TableCell>
                          <TableCell align="right">{comparison.snapshot1.name}</TableCell>
                          <TableCell align="right">{comparison.snapshot2.name}</TableCell>
                          <TableCell align="right">売上差分</TableCell>
                          <TableCell align="right">粗利({comparison.snapshot1.name})</TableCell>
                          <TableCell align="right">粗利({comparison.snapshot2.name})</TableCell>
                          <TableCell align="right">粗利差分</TableCell>
                          <TableCell align="center">行動履歴</TableCell>
                          <TableCell>変更原因</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comparison.cases.map((c) => (
                          <TableRow
                            key={c.caseId}
                            hover
                            onClick={() => navigate(`/sales/cases/${c.caseId}`)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{c.caseNo}</TableCell>
                            <TableCell>{c.caseName}</TableCell>
                            <TableCell align="right">{formatCurrency(c.sales1)}</TableCell>
                            <TableCell align="right">{formatCurrency(c.sales2)}</TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: getDiffColor(c.salesDiff), fontWeight: 'bold' }}
                            >
                              {c.salesDiff >= 0 ? '+' : ''}
                              {formatCurrency(c.salesDiff)}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(c.profit1)}</TableCell>
                            <TableCell align="right">{formatCurrency(c.profit2)}</TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: getDiffColor(c.profitDiff), fontWeight: 'bold' }}
                            >
                              {c.profitDiff >= 0 ? '+' : ''}
                              {formatCurrency(c.profitDiff)}
                            </TableCell>
                            <TableCell align="center">
                              {(c.activityCount ?? 0) > 0 ? (
                                <Chip label={`${c.activityCount}件`} size="small" variant="outlined" />
                              ) : (
                                <Typography variant="body2" color="text.secondary">0件</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {c.changeReasons?.map((reason, i) => {
                                const reasonColors: Record<string, string> = {
                                  '新規追加': '#4CAF50',
                                  '受注': '#1976D2',
                                  '失注': '#F44336',
                                  '削除': '#9E9E9E',
                                  '保留': '#FF9800',
                                  '売上変更': '#7B1FA2',
                                  '原価変更': '#E91E63',
                                };
                                return (
                                  <Chip
                                    key={i}
                                    label={reason}
                                    size="small"
                                    sx={{
                                      mr: 0.5,
                                      mb: 0.5,
                                      backgroundColor: reasonColors[reason] || '#e0e0e0',
                                      color: '#fff',
                                    }}
                                  />
                                );
                              })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            {/* 行動履歴明細 */}
            {(comparison.summary.activities?.length ?? 0) > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    行動履歴明細（{comparison.summary.activities.length}件）
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>日付</TableCell>
                          <TableCell>案件番号</TableCell>
                          <TableCell>案件名</TableCell>
                          <TableCell>種別</TableCell>
                          <TableCell>内容</TableCell>
                          <TableCell>記録者</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comparison.summary.activities.map((act) => (
                          <TableRow
                            key={act.activityId}
                            hover
                            onClick={() => navigate(`/sales/cases/${act.caseId}`)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {formatDateJST(act.activityDate)}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{act.caseNo}</TableCell>
                            <TableCell>{act.caseName}</TableCell>
                            <TableCell>
                              <Chip label={act.activityType} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {act.description || '-'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{act.createdByName}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </TabPanel>

      {/* 作成ダイアログ */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>スナップショット作成</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="スナップショット名"
              value={snapshotName}
              onChange={(e) => setSnapshotName(e.target.value)}
              placeholder="例: 2026年度年計（確定版）"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="説明（任意）"
              value={snapshotDescription}
              onChange={(e) => setSnapshotDescription(e.target.value)}
              multiline
              rows={3}
              placeholder="このスナップショットについての補足説明"
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              現在の全案件の計画データがスナップショットとして保存されます。
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!snapshotName.trim()}>
            作成
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>スナップショット削除</DialogTitle>
        <DialogContent>
          <Typography>
            「{deleteTarget?.snapshotName}」を削除しますか？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesSnapshotsPage;
